import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, StatCard, Button, Badge } from '@/backoffice/components/ui';
import { DriverMissionCard } from '@/backoffice/components/driver/DriverMissionCard';
import { DriverGpsTracker } from '@/backoffice/components/driver/DriverGpsTracker';
import { formatCurrency } from '@/backoffice/utils';
import {
  fetchDriverDashboard,
  fetchDriverMissions,
  acceptDriverMission,
  rejectDriverMission,
  completeDriverMission,
  type DriverDashboardData,
  type DriverMission,
} from '@/domains/driver/services/driver.service';
import {
  Tractor,
  Package,
  MapPin,
  Truck,
  Wallet,
  Clock,
  ArrowRight,
  Route,
  TrendingUp,
  Calendar,
  Bell,
  Loader2,
} from 'lucide-react';

export function ProDriverDashboard() {
  const [dashboard, setDashboard] = useState<DriverDashboardData | null>(null);
  const [missions, setMissions] = useState<DriverMission[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = () => {
    Promise.all([fetchDriverDashboard(), fetchDriverMissions()])
      .then(([dash, list]) => {
        setDashboard(dash);
        setMissions(list);
      })
      .catch(() => {
        setDashboard(null);
        setMissions([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const stats = dashboard?.stats;
  const pendingMissions = missions.filter((o) => o.status === 'pending').slice(0, 3);
  const activeMissions = missions.filter((o) => o.status === 'in_progress');

  const handleAccept = async (id: number) => {
    setActionLoading(true);
    try {
      await acceptDriverMission(id);
      load();
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id: number) => {
    setActionLoading(true);
    try {
      await rejectDriverMission(id);
      load();
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (id: number) => {
    setActionLoading(true);
    try {
      await completeDriverMission(id);
      load();
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="pro_driver" title="Tableau de bord">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="pro_driver" title="Tableau de bord">
      <DriverGpsTracker />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-700">Gains du jour</p>
              <p className="text-2xl font-bold text-emerald-800">{formatCurrency(stats?.today_earnings ?? 0)}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-emerald-200 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-emerald-700" />
            </div>
          </div>
        </Card>

        <StatCard
          title="Livraisons aujourd'hui"
          value={stats?.today_deliveries ?? 0}
          icon={<Package className="w-6 h-6 text-blue-600" />}
          iconBg="bg-blue-50"
        />

        <StatCard
          title="Missions en attente"
          value={stats?.pending_missions ?? pendingMissions.length}
          icon={<Clock className="w-6 h-6 text-amber-600" />}
          iconBg="bg-amber-50"
        />

        <StatCard
          title="Note moyenne"
          value={`${stats?.average_rating ?? 0}/5`}
          icon={<Truck className="w-6 h-6 text-purple-600" />}
          iconBg="bg-purple-50"
        />
      </div>

      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900">Résumé du jour</h2>
          <div className="flex items-center gap-2 text-2xl font-bold text-emerald-600">
            <Wallet className="w-6 h-6" />
            {formatCurrency(stats?.today_earnings ?? 0)}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Livraisons complétées</p>
            <p className="text-xl font-semibold text-gray-900">{stats?.today_deliveries ?? 0}</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Taux d'acceptation</p>
            <p className="text-xl font-semibold text-gray-900">{stats?.acceptance_rate ?? 0}%</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Distance parcourue</p>
            <p className="text-xl font-semibold text-gray-900">{stats?.distance_km_today ?? 0} km</p>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Nouvelles missions
            </h2>
            <Badge variant="warning">{pendingMissions.length}</Badge>
          </div>

          {pendingMissions.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune mission en attente</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingMissions.map((mission) => (
                <DriverMissionCard
                  key={mission.id}
                  mission={mission}
                  compact
                  actionLoading={actionLoading}
                  onAccept={() => handleAccept(mission.id)}
                  onReject={() => handleReject(mission.id)}
                />
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-500" />
              En cours
            </h2>
            <Link to="/dashboard/livreur-pro/itineraire">
              <Button variant="outline" size="sm">Voir feuille de route</Button>
            </Link>
          </div>

          {activeMissions.length === 0 ? (
            <div className="text-center py-8">
              <Route className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune livraison en cours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeMissions.map((mission) => (
                <div key={mission.id} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-gray-900">{mission.code}</p>
                    <Badge variant="primary" dot>En transit</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{mission.pickup.zone}</span>
                    <ArrowRight className="w-4 h-4" />
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    <span>{mission.dropoff.zone}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-semibold text-emerald-600">{formatCurrency(mission.price)}</p>
                    <Button
                      variant="primary"
                      size="sm"
                      isLoading={actionLoading}
                      onClick={() => handleComplete(mission.id)}
                    >
                      Marquer comme livrée
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/dashboard/livreur-pro/itineraire">
          <Card className="hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Route className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Feuille de route</p>
                <p className="text-sm text-gray-500">Optimiser itinéraire</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/dashboard/livreur-pro/revenus">
          <Card className="hover:border-emerald-300 hover:shadow-sm transition-all cursor-pointer h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Mes gains</p>
                <p className="text-sm text-gray-500">{formatCurrency(stats?.week_earnings ?? 0)} cette semaine</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/dashboard/livreur-pro/zones">
          <Card className="hover:border-purple-300 hover:shadow-sm transition-all cursor-pointer h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Mes zones</p>
                <p className="text-sm text-gray-500">{stats?.coverage_zones_count ?? 0} zones actives</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/dashboard/livreur-pro/assurance">
          <Card className="hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer h-full">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Tractor className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Assurance</p>
                <p className="text-sm text-gray-500">
                  {stats?.insurance_expiry ? `Expire le ${stats.insurance_expiry}` : 'Voir le statut'}
                </p>
              </div>
            </div>
          </Card>
        </Link>
      </div>
    </DashboardLayout>
  );
}
